import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, PackageCheck, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function BulkPreorder() {
  const [products, setProducts] = useState([]);
  const [usedIds, setUsedIds] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [estimatedShipDate, setEstimatedShipDate] = useState("");
  const [preOrderPrice, setPreOrderPrice] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [preOrderCap, setPreOrderCap] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.listProducts().catch(() => []), api.listCampaigns().catch(() => [])])
      .then(([prods, camps]) => {
        setProducts(prods);
        setUsedIds(new Set(camps.map((c) => c.productId)));
      })
      .finally(() => setLoading(false));
  }, []);

  const available = useMemo(() => products.filter((p) => !usedIds.has(p.id)), [products, usedIds]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected = available.length > 0 && selected.size === available.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(available.map((p) => p.id)));
  };

  const submit = async () => {
    if (selected.size === 0) return toast.error("Select at least one product");
    if (!estimatedShipDate) return toast.error("Estimated ship date is required");
    setSubmitting(true);
    try {
      const res = await api.bulkPreorder({
        productIds: [...selected],
        estimatedShipDate,
        preOrderPrice: preOrderPrice ? Number(preOrderPrice) : null,
        depositAmount: depositAmount ? Number(depositAmount) : null,
        preOrderCap: preOrderCap ? Number(preOrderCap) : null,
      });
      toast.success(`${res.createdCount} sent to pre-order${res.skippedCount ? ` · ${res.skippedCount} skipped` : ""}`);
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create pre-orders");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-preorder-page">
      <div>
        <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Bulk pre-order</h1>
        <p className="text-sm text-gray-600 mt-2">Select products and open them all for pre-order at once — no voting.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Product picker */}
        <section className="lp-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold tracking-tight">Products</h2>
            {available.length > 0 && (
              <button type="button" onClick={toggleAll} className="text-xs text-gray-600 hover:text-gray-900" data-testid="bulk-select-all">
                {allSelected ? "Clear all" : "Select all"}
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-sm text-gray-500 py-10 text-center">Loading products…</div>
          ) : available.length === 0 ? (
            <div className="text-sm text-gray-500 py-10 text-center" data-testid="bulk-no-products">
              Every product already has a campaign. Nothing left to add.
            </div>
          ) : (
            <div className="space-y-2 max-h-[520px] overflow-auto pr-1">
              {available.map((p) => {
                const isSel = selected.has(p.id);
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    data-testid={`bulk-product-${p.id}`}
                    className={`w-full flex items-center gap-4 p-3 rounded-xl border text-left transition-all ${isSel ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${isSel ? "bg-gray-900 border-gray-900" : "border-gray-300"}`}>
                      {isSel && <Check size={13} className="text-white" />}
                    </div>
                    <img src={p.imageUrl} alt="" className="w-12 h-12 object-cover rounded-lg border border-gray-100" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">{p.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{p.vendor} · ${p.priceRange.min.toFixed(2)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Shared settings */}
        <aside className="space-y-6">
          <div className="lp-card p-6 sticky top-6">
            <div className="lp-section-label">Pre-order settings</div>
            <p className="text-xs text-gray-500 mt-2 mb-4">Applied to every selected product.</p>

            <label className="lp-label" htmlFor="bulk-ship-date">Estimated ship date</label>
            <input id="bulk-ship-date" type="date" className="lp-input" data-testid="bulk-ship-date"
              value={estimatedShipDate} onChange={(e) => setEstimatedShipDate(e.target.value)} />

            <label className="lp-label mt-4" htmlFor="bulk-price">Pre-order price ($, optional)</label>
            <input id="bulk-price" type="number" step="0.01" className="lp-input" data-testid="bulk-price"
              value={preOrderPrice} onChange={(e) => setPreOrderPrice(e.target.value)} placeholder="Use each product's price" />

            <label className="lp-label mt-4" htmlFor="bulk-deposit">Deposit ($, optional)</label>
            <input id="bulk-deposit" type="number" step="0.01" className="lp-input" data-testid="bulk-deposit"
              value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="Full payment" />

            <label className="lp-label mt-4" htmlFor="bulk-cap">Pre-order cap (optional)</label>
            <input id="bulk-cap" type="number" className="lp-input" data-testid="bulk-cap"
              value={preOrderCap} onChange={(e) => setPreOrderCap(e.target.value)} placeholder="Unlimited" />

            <button
              type="button"
              onClick={submit}
              disabled={submitting || selected.size === 0}
              className="lp-btn-primary w-full justify-center mt-6"
              data-testid="bulk-submit-btn"
            >
              {submitting ? "Creating…" : (<><PackageCheck size={16} /> Send {selected.size || ""} to pre-order</>)}
            </button>
            <div className="text-[11px] text-gray-500 mt-3 text-center">
              Products already in a campaign are hidden.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
